import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AiWeltService } from './ai-welt.service';

/**
 * Globaler Activity-Tracker für AI Welt.
 * Throttled: Maus max. 1×/2s, Tippen 1×/1s, Heartbeat alle 12s.
 */
@Injectable({ providedIn: 'root' })
export class AiWeltPresenceTrackerService implements OnDestroy {
  private started = false;
  private route = '';
  private routeLabel = '';
  private orderId: number | null = null;
  private orderLabel = '';
  private lastMouseAt = 0;
  private lastTypingAt = 0;
  private lastHeartbeatAt = 0;
  private idleTimer: any = null;
  private heartbeatTimer: any = null;
  private unreadTimer: any = null;
  private routerSub: Subscription | null = null;
  private boundMouse: any;
  private boundKey: any;
  private boundVis: any;

  /** Unread-Badge für Mitarbeiter-Chat */
  unreadCount = 0;

  constructor(
    private aiWelt: AiWeltService,
    private router: Router,
    private zone: NgZone
  ) {}

  start(): void {
    if (this.started || typeof window === 'undefined') {
      return;
    }
    const user = this.readUser();
    if (!user) {
      return;
    }
    this.started = true;
    this.applyRoute(this.router.url);

    this.zone.runOutsideAngular(() => {
      this.boundMouse = () => this.onMouse();
      this.boundKey = () => this.onTyping();
      this.boundVis = () => this.onVisibility();
      window.addEventListener('mousemove', this.boundMouse, { passive: true });
      window.addEventListener('keydown', this.boundKey, { passive: true });
      document.addEventListener('visibilitychange', this.boundVis);

      this.heartbeatTimer = setInterval(() => this.send('idle', true), 12000);
      this.unreadTimer = setInterval(() => this.pollUnread(), 20000);
    });

    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.applyRoute(e.urlAfterRedirects || e.url);
        this.send('focus', true);
      });

    this.send('focus', true);
    this.pollUnread();
  }

  stop(): void {
    if (!this.started) {
      return;
    }
    this.started = false;
    window.removeEventListener('mousemove', this.boundMouse);
    window.removeEventListener('keydown', this.boundKey);
    document.removeEventListener('visibilitychange', this.boundVis);
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    if (this.unreadTimer) {
      clearInterval(this.unreadTimer);
    }
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
    this.routerSub?.unsubscribe();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private readUser(): any {
    try {
      return JSON.parse(localStorage.getItem('currentUser') || 'null');
    } catch {
      return null;
    }
  }

  private applyRoute(url: string): void {
    const clean = (url || '').split('?')[0].replace(/^#/, '');
    this.route = clean.startsWith('/') ? clean : '/' + clean;
    this.routeLabel = this.labelForRoute(this.route);

    const orderMatch =
      this.route.match(/\/order(?:s)?\/(\d+)/i) ||
      this.route.match(/order[_-]?id[=/](\d+)/i) ||
      (url || '').match(/[?&]order_id=(\d+)/i);
    const hashOrder = this.route.match(/(?:order|auftrag)[^0-9]*(\d{3,})/i);

    if (orderMatch) {
      this.orderId = +orderMatch[1];
      // Label wird serverseitig auf order_number aufgelöst
      this.orderLabel = '';
    } else if (hashOrder && /disposition|order|auftrag|dispo/i.test(this.route)) {
      this.orderId = +hashOrder[1];
      this.orderLabel = '';
    } else {
      this.orderId = null;
      this.orderLabel = '';
    }
  }

  private labelForRoute(url: string): string {
    const u = (url || '').toLowerCase();
    if (u.includes('ai-welt')) return 'AI Welt';
    if (u.includes('/stats')) {
      if (u.includes('order')) return 'Stats · Aufträge';
      if (u.includes('tool')) return 'Stats · Tools';
      return 'Statistiken';
    }
    if (u.includes('/agenda')) return 'Agenda';
    if (u.includes('disposition') || u.includes('order-disposition')) return 'Disposition';
    if (u.includes('/order')) return 'Aufträge';
    if (u.includes('/dashboard')) return 'Dashboard';
    if (u.includes('/personal') || u.includes('/resource')) return 'Personal';
    if (u.includes('/team')) return 'Teams';
    if (u.includes('/chats')) return 'Chats';
    if (u.includes('/login')) return 'Login';
    if (u.includes('/crm') || u.includes('customer')) return 'CRM';
    const parts = u.replace(/^#?\/?/, '').split('/').filter(Boolean);
    if (!parts.length) {
      return 'App';
    }
    // Letztes sinnvolles Segment als Ort
    const last = parts[parts.length - 1].replace(/[-_]/g, ' ');
    return last.charAt(0).toUpperCase() + last.slice(1);
  }

  private onMouse(): void {
    const now = Date.now();
    if (now - this.lastMouseAt < 2000) {
      return;
    }
    this.lastMouseAt = now;
    this.send('mouse');
    this.scheduleIdle();
  }

  private onTyping(): void {
    const now = Date.now();
    if (now - this.lastTypingAt < 1000) {
      return;
    }
    this.lastTypingAt = now;
    this.send('typing');
    this.scheduleIdle();
  }

  private onVisibility(): void {
    if (document.visibilityState === 'visible') {
      this.send('focus', true);
    }
  }

  private scheduleIdle(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
    this.idleTimer = setTimeout(() => this.send('idle'), 2500);
  }

  private send(activity: 'idle' | 'mouse' | 'typing' | 'focus', force = false): void {
    if (!this.started || !this.readUser()) {
      return;
    }
    const now = Date.now();
    if (!force && activity === 'idle' && now - this.lastHeartbeatAt < 8000) {
      return;
    }
    if (activity === 'idle' || force) {
      this.lastHeartbeatAt = now;
    }
    this.aiWelt.postPresence({
      route: this.route,
      route_label: this.routeLabel,
      order_id: this.orderId,
      order_label: this.orderLabel || undefined,
      activity,
    }).subscribe({ error: () => {} });
  }

  private pollUnread(): void {
    if (!this.readUser()) {
      return;
    }
    this.aiWelt.getUnread().subscribe({
      next: (res) => {
        this.zone.run(() => {
          this.unreadCount = res?.count || 0;
        });
      },
      error: () => {},
    });
  }
}
